import { TILE } from './layout';

/**
 * Where the map sits inside the canvas, and how big it is drawn.
 *
 * Two modes, because a television and a phone want opposite things from the
 * same room:
 *
 *  - `fit` shows the whole map at once. It is what a 4:3 frame on a TV or a
 *    tablet wants, and it is the only honest choice while the map is being
 *    *edited* — a brush that can only reach the part of the grid you happen to
 *    be standing near is not an editor.
 *  - `cover` scales until the map covers the frame in both axes and then pans
 *    to keep the pet in the middle, stopping at the edges of the map. That is
 *    what makes a portrait phone feel like a game rather than a dollhouse: the
 *    room fills the screen instead of floating in a band of black, and the cat
 *    is drawn big enough to have a face.
 *
 * Pure and Pixi-free on purpose: this is the one piece of the scene layer whose
 * arithmetic is easy to get subtly wrong, so it is the one piece with tests.
 * Everything here is in *design space* — the 32px-per-tile coordinates from
 * `layout.ts` — except the returned `x`/`y`, which are canvas pixels.
 */

export type CameraMode = 'fit' | 'cover';

export interface Size {
  width: number;
  height: number;
}

export interface Point {
  x: number;
  y: number;
}

export interface CameraTransform {
  scale: number;
  /** Canvas-pixel offset for the map container. */
  x: number;
  y: number;
}

/**
 * The floor of how far `cover` may zoom, expressed as tiles rather than a bare
 * multiplier so the intent survives a change to `TILE`.
 *
 * A frame far taller than it is wide — a narrow desktop window, a phone held in
 * portrait with the browser chrome collapsed — asks `cover` for a scale that
 * would leave three or four tiles on screen. Covering the frame is not worth
 * turning the room into a keyhole, so past this point the map is centred and
 * bars come back.
 */
const MIN_VISIBLE_TILES = 6;

const IDENTITY: CameraTransform = { scale: 1, x: 0, y: 0 };

/**
 * @param screen The canvas, in pixels.
 * @param world  The map's design-space size — see `worldPixelSize`.
 * @param focus  The design-space point to keep centred. Ignored by `fit`, and
 *               ignored on any axis where the map does not overflow the frame.
 */
export function cameraTransform(
  mode: CameraMode,
  screen: Size,
  world: Size,
  focus: Point,
): CameraTransform {
  // A canvas is 0x0 for the frame between mounting and the first resize, and a
  // world cannot be empty — but both would divide by zero here, so neither is
  // assumed.
  if (screen.width <= 0 || screen.height <= 0 || world.width <= 0 || world.height <= 0) {
    return IDENTITY;
  }

  const scale = scaleFor(mode, screen, world);

  return {
    scale,
    x: place(screen.width, world.width * scale, focus.x * scale),
    y: place(screen.height, world.height * scale, focus.y * scale),
  };
}

function scaleFor(mode: CameraMode, screen: Size, world: Size): number {
  const fit = Math.min(screen.width / world.width, screen.height / world.height);
  if (mode === 'fit') return fit;

  const cover = Math.max(screen.width / world.width, screen.height / world.height);
  const capped = Math.min(
    cover,
    Math.min(screen.width, screen.height) / (MIN_VISIBLE_TILES * TILE),
  );

  // Never below `fit`: the cap is a limit on zooming *in*, and letting it push
  // the map smaller than the frame would only add bars on both axes.
  return Math.max(fit, capped);
}

/**
 * One axis: centre the map when it is smaller than the frame, otherwise centre
 * the pet and stop at the map's edge.
 *
 * The clamp is what stops the camera drifting off the map when the pet walks
 * into a corner — without it the room would slide away from a wall and leave
 * empty space beyond it.
 */
function place(screenLength: number, drawnLength: number, focusOffset: number): number {
  if (drawnLength <= screenLength) return (screenLength - drawnLength) / 2;
  const centred = screenLength / 2 - focusOffset;
  return Math.min(0, Math.max(screenLength - drawnLength, centred));
}
